import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import {
  ConfigStripConfigurationKeys,
  ConfigurationsService,
  CONFIG_STRIP_FEATURE,
} from '@epicuro-next/platform/configuration';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { BasketAccountAddress, Prepack } from 'graphql-api/gql-api-interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface ICellCoord {
  x: number | null;
  y: number | null;
}

interface ICoords {
  start: ICellCoord;
  end: ICellCoord;
}

@Component({
  selector: 'epc-advanced-mdo-grid',
  templateUrl: './advanced-mdo-grid.component.html',
  styleUrls: ['./advanced-mdo-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AdvancedMdoGridComponent implements OnInit, OnDestroy {
  @Input() public set rows(value: Prepack[]) {
    this.rowItems = value?.map((row) => ({ ...row, opened: false })) ?? [];
  }

  public get columnConfig() {
    return this.portable
      ? '12rem repeat(' + (this.columns?.length || 1) + ', minmax(3rem, 1fr))'
      : '12rem repeat(' + (this.columns?.length || 1) + ', minmax(10rem, 1fr))';
  }
  @Input() public columns: BasketAccountAddress[];
  public rowItems: Prepack[];
  @Input() public headerName: string;
  @Input() public cellTemplate: TemplateRef<HTMLElement>;
  @Input() public rowHeadTemplate: TemplateRef<HTMLElement>;
  @Input() public shopTotals: { [key: string]: number };
  @Input() public rowDetailsTemplate: TemplateRef<HTMLElement>;
  // used for compact mode (leave only address id in column headers)
  @Input() public portable = false;
  @Input() public restrictVerticalCopy = false;
  @Output() public valuesCopied = new EventEmitter();
  @Output() public clearShop = new EventEmitter();
  public copyCoords: ICoords = {
    start: { x: null, y: null },
    end: { x: null, y: null },
  };

  // duplicate of copyCoords, but with a twist:
  // start coords are always on top left end coords are always on bottom left
  public displayCopyCoords: ICoords = {
    start: { x: null, y: null },
    end: { x: null, y: null },
  };
  public isShiftKeyCurrentlyPressed = false;
  public hasFocus = false;
  public startCoords = new Subject<ICellCoord>();
  private destroy$ = new Subject<void>();
  public readonly copyIcon$ = this.configurationsService.getConfigValue({
    featureName: ConfigStripConfigurationKeys.MultipleSizeCopyIcon,
    namespace: CONFIG_STRIP_FEATURE,
    defaultValue: ['fas', 'copy'],
  });

  @HostListener('window:keydown.shift', ['$event'])
  @HostListener('window:keyup.shift', ['$event'])
  public onShiftKeyEvents(event: KeyboardEvent) {
    this.isShiftKeyCurrentlyPressed = event.shiftKey;
  }

  public trackByFn(index: number) {
    return index;
  }

  constructor(private configurationsService: ConfigurationsService) {}

  public ngOnInit() {
    this.startCoords.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.copyCoords.start = data;
    });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setCopyStartCoords(x: number | null, y: number | null) {
    this.copyCoords = {
      ...this.copyCoords,
      start: { x, y },
    };
  }

  public setCopyEndCoords(x: number | null, y: number | null) {
    if (this.restrictVerticalCopy && y !== this.copyCoords.start.y) {
      return;
    }

    this.copyCoords = {
      ...this.copyCoords,
      end: { x, y },
    };

    this.updateDisplayCopyCoords();
  }

  public isInCoords(x: number, y: number) {
    // coordinates of source cell
    const startX = this.copyCoords.start.x;
    const startY = this.copyCoords.start.y;
    // coordinates of end cell (opposite square corner)
    const endX = this.copyCoords.end.x;
    const endY = this.copyCoords.end.y;

    if (
      isNullish(startX) ||
      isNullish(startY) ||
      isNullish(endX) ||
      isNullish(endY)
    ) {
      return false;
    }

    if (this.restrictVerticalCopy) {
      return (
        y === startY &&
        ((x >= startX && x <= endX) || (x <= startX && x >= endX))
      );
    }

    // this function is run by every cell and checks whenever cell is inside start and end coordinates
    // this check uses rowIndex as y and colIndex as x
    // first it checks if y is inside coords, and then checks x for both ways

    return (
      // check if cell is below source cell (if user copies from top to bottom)
      (y >= startY &&
        y <= endY &&
        // check x coordinate for both scenarios (copy from left to right and opposite)
        ((x >= startX && x <= endX) || (x <= startX && x >= endX))) ||
      // check if cell is above source cell (if user copies from bottom to top)
      (y <= startY &&
        y >= endY &&
        // check x coordinate for both scenarios (copy from left to right and opposite)
        ((x >= startX && x <= endX) || (x <= startX && x >= endX)))
    );
  }

  public isCopyVisible(y: number) {
    if (
      !this.isShiftKeyCurrentlyPressed ||
      this.copyCoords.start.x === null ||
      this.copyCoords.start.y === null
    ) {
      return false;
    }

    if (this.restrictVerticalCopy) {
      return y === this.copyCoords.start.y;
    }

    return true;
  }

  public copy() {
    const { start } = this.copyCoords;
    if (isNonNullish(start.x) && isNonNullish(start.y)) {
      this.valuesCopied.emit({
        from: this.copyCoords.start,
        to: this.copyCoords.end,
      });
      this.setCopyStartCoords(null, null);
      this.setCopyEndCoords(null, null);
      this.toggleCellFocus(false, { x: null, y: null });
    }
  }

  public toggleCellFocus(hasFocus: boolean, cell: ICellCoord) {
    this.hasFocus = hasFocus;
    this.startCoords.next(cell);
  }

  public updateDisplayCopyCoords() {
    const { start, end } = this.copyCoords;
    const startX = start.x;
    const endX = end.x;
    const startY = start.y;
    const endY = end.y;

    if (
      isNullish(startX) ||
      isNullish(endX) ||
      isNullish(startY) ||
      isNullish(endY)
    )
      return;

    this.displayCopyCoords = { start: { ...start }, end: { ...end } };

    if (startX > endX) {
      this.displayCopyCoords.start.x = endX;
      this.displayCopyCoords.end.x = startX;
    }

    if (startY > endY) {
      this.displayCopyCoords.start.y = endY;
      this.displayCopyCoords.end.y = startY;
    }
  }
}
